"use client";

import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { cn, errorIntoString } from "@/lib/utils";
import { ga4GenerateLead } from "@/lib/analytics/ga4-ecommerce";
import {
	useBookCalendarCallMutation,
	useGetCalendarSlotsQuery,
	useGetCalendarStatusQuery,
	type CalendarSlot,
} from "@/features/calendar/calendarInjection";

type BookCallDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	serviceTitle?: string;
};

function ymd(date: Date): string {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, "0");
	const d = String(date.getDate()).padStart(2, "0");
	return `${y}-${m}-${d}`;
}

function startOfMonth(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addDays(date: Date, count: number): Date {
	return new Date(date.getFullYear(), date.getMonth(), date.getDate() + count);
}

function addMonths(date: Date, count: number): Date {
	return new Date(date.getFullYear(), date.getMonth() + count, 1);
}

const BOOKING_WINDOW_DAYS = 21;

function monthRange(month: Date): { from: string; to: string } {
	const start = startOfMonth(month);
	const end = new Date(month.getFullYear(), month.getMonth() + 1, 0);
	return { from: ymd(start), to: ymd(end) };
}

export default function BookCallDialog({
	open,
	onOpenChange,
	serviceTitle,
}: BookCallDialogProps) {
	const { toast } = useToast();
	const [month, setMonth] = useState(() => startOfMonth(new Date()));
	const [selectedDay, setSelectedDay] = useState(() => ymd(new Date()));
	const [selectedSlot, setSelectedSlot] = useState<CalendarSlot | null>(null);
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [email, setEmail] = useState("");
	const [notes, setNotes] = useState("");
	const range = monthRange(month);
	const { data: status } = useGetCalendarStatusQuery(undefined, { skip: !open });
	const {
		data: slotsData,
		isFetching: slotsLoading,
		isError: slotsError,
		error: slotsErrorDetail,
	} = useGetCalendarSlotsQuery(range, {
		skip: !open || !status?.connected,
	});
	const [bookCall, { isLoading: isBooking }] = useBookCalendarCallMutation();
	const timezone = slotsData?.timezone || status?.timezone || "Europe/London";
	const slotsByDay = useMemo(() => {
		const map = new Map<string, CalendarSlot[]>();
		for (const slot of slotsData?.slots ?? []) {
			const day = new Intl.DateTimeFormat("en-CA", {
				timeZone: timezone,
				year: "numeric",
				month: "2-digit",
				day: "2-digit",
			}).format(new Date(slot.start));
			const list = map.get(day) ?? [];
			list.push(slot);
			map.set(day, list);
		}
		return map;
	}, [slotsData?.slots, timezone]);
	const daySlots = slotsByDay.get(selectedDay) ?? [];
	const today = ymd(new Date());
	const lastBookable = ymd(addDays(new Date(), BOOKING_WINDOW_DAYS));
	const thisMonth = startOfMonth(new Date());
	const canPrevMonth = month.getTime() > thisMonth.getTime();
	const canNextMonth = ymd(addMonths(month, 1)) <= lastBookable;
	const weekdayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
	const cells = useMemo(() => {
		const first = startOfMonth(month);
		const mondayIndex = (first.getDay() + 6) % 7;
		const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
		const items: Array<{ day: string | null; dateNum: number | null }> = [];
		for (let i = 0; i < mondayIndex; i += 1) items.push({ day: null, dateNum: null });
		for (let dateNum = 1; dateNum <= daysInMonth; dateNum += 1) {
			const date = new Date(month.getFullYear(), month.getMonth(), dateNum);
			items.push({ day: ymd(date), dateNum });
		}
		return items;
	}, [month]);

	const monthLabel = new Intl.DateTimeFormat("en-GB", {
		month: "long",
		year: "numeric",
	}).format(month);

	const submit = async () => {
		if (!selectedSlot) {
			toast({
				variant: "destructive",
				title: "Pick a time",
				description: "Choose an available slot first.",
			});
			return;
		}
		try {
			await bookCall({
				start: selectedSlot.start,
				end: selectedSlot.end,
				firstName: firstName.trim(),
				lastName: lastName.trim(),
				email: email.trim(),
				notes: notes.trim() || undefined,
				serviceTitle: serviceTitle?.trim() || undefined,
				sourceUrl: typeof window !== "undefined" ? window.location.href : undefined,
			}).unwrap();
			ga4GenerateLead({
				lead_source: "book_a_call",
				item_name: serviceTitle?.trim() || undefined,
			});
			toast({
				title: "Call booked",
				description: "A Google Calendar invite is on its way, including a Meet link.",
			});
			setSelectedSlot(null);
			setNotes("");
			onOpenChange(false);
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Could not book that time",
				description: errorIntoString(error) || "Please pick another slot.",
			});
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[90vh] overflow-y-auto border-secondary/25 bg-background sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>Book a Call</DialogTitle>
					<DialogDescription>
						Pick an available slot on our calendar. All times are in Europe/London.
					</DialogDescription>
				</DialogHeader>

				{status && !status.connected ? (
					<p className="text-sm text-muted-foreground">
						The booking calendar is not connected yet. Connect it in Settings with the
						Google Workspace account.
					</p>
				) : (
					<div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
						<div className="space-y-3">
							<div className="flex items-center justify-between">
								<button
									type="button"
									disabled={!canPrevMonth}
									className={cn(
										"rounded-full p-1 text-secondary hover:bg-secondary/10",
										!canPrevMonth && "pointer-events-none opacity-40",
									)}
									onClick={() => {
										setMonth((current) => addMonths(current, -1));
										setSelectedSlot(null);
									}}
									aria-label="Previous month"
								>
									<ChevronLeft className="size-5" />
								</button>
								<p className="text-sm font-semibold uppercase tracking-wide text-foreground">
									{monthLabel}
								</p>
								<button
									type="button"
									disabled={!canNextMonth}
									className={cn(
										"rounded-full p-1 text-secondary hover:bg-secondary/10",
										!canNextMonth && "pointer-events-none opacity-40",
									)}
									onClick={() => {
										setMonth((current) => addMonths(current, 1));
										setSelectedSlot(null);
									}}
									aria-label="Next month"
								>
									<ChevronRight className="size-5" />
								</button>
							</div>
							<div className="grid grid-cols-7 gap-1 text-center text-[11px] font-medium text-muted-foreground">
								{weekdayLabels.map((label) => (
									<span key={label}>{label}</span>
								))}
							</div>
							<div className="grid grid-cols-7 gap-1">
								{cells.map((cell, index) => {
									if (!cell.day) {
										return <span key={`empty-${index}`} className="h-9" />;
									}
									const isSelected = selectedDay === cell.day;
									const isToday = cell.day === today;
									const [year, monthNum, dateNumValue] = cell.day.split("-").map(Number);
									const weekday = new Date(year, monthNum - 1, dateNumValue).getDay();
									const isWeekend = weekday === 0 || weekday === 6;
									const disabled = cell.day < today || cell.day > lastBookable || isWeekend;
									return (
										<button
											key={cell.day}
											type="button"
											disabled={disabled}
											onClick={() => {
												setSelectedDay(cell.day as string);
												setSelectedSlot(null);
											}}
											className={cn(
												"h-9 rounded-full text-sm",
												isSelected && "bg-secondary text-secondary-foreground",
												!isSelected && isToday && "border border-secondary text-secondary",
												!isSelected && !disabled && !isToday && "text-secondary hover:bg-secondary/10",
												disabled && "text-muted-foreground/50",
											)}
										>
											{cell.dateNum}
										</button>
									);
								})}
							</div>
							<p className="text-xs text-muted-foreground">
								{slotsLoading
									? "Loading times..."
									: slotsError
										? errorIntoString(slotsErrorDetail) ||
											"Could not load times. Reconnect Google Calendar in Settings."
										: `${daySlots.length} times on this day`}
							</p>
							<div className="flex flex-wrap gap-2">
								{daySlots.map((slot) => (
									<button
										key={slot.start}
										type="button"
										onClick={() => setSelectedSlot(slot)}
										className={cn(
											"rounded-full border border-secondary/30 px-3 py-1.5 text-xs font-semibold",
											selectedSlot?.start === slot.start
												? "bg-secondary text-secondary-foreground"
												: "text-foreground hover:bg-secondary/10",
										)}
									>
										{slot.label}
									</button>
								))}
							</div>
						</div>
						<div className="space-y-3">
							<div className="grid grid-cols-2 gap-3">
								<label className="grid gap-1.5 text-sm">
									<span>First Name</span>
									<Input
										value={firstName}
										onChange={(event) => setFirstName(event.target.value)}
										autoComplete="given-name"
									/>
								</label>
								<label className="grid gap-1.5 text-sm">
									<span>Last Name</span>
									<Input
										value={lastName}
										onChange={(event) => setLastName(event.target.value)}
										autoComplete="family-name"
									/>
								</label>
							</div>
							<label className="grid gap-1.5 text-sm">
								<span>Email</span>
								<Input
									type="email"
									value={email}
									onChange={(event) => setEmail(event.target.value)}
								/>
							</label>
							<label className="grid gap-1.5 text-sm">
								<span>Notes (optional)</span>
								<Textarea
									value={notes}
									onChange={(event) => setNotes(event.target.value)}
									rows={4}
								/>
							</label>
							<Button
								type="button"
								onClick={() => void submit()}
								disabled={
									isBooking ||
									!selectedSlot ||
									!firstName.trim() ||
									!lastName.trim() ||
									!email.includes("@")
								}
								className="h-11 w-full rounded-full bg-secondary text-xs font-semibold text-secondary-foreground hover:bg-secondary/90 sm:text-sm"
							>
								{isBooking ? "Booking..." : "Confirm call"}
							</Button>
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
